<script setup lang="ts">
import { ref, computed } from 'vue'

type HideDetails = boolean | 'auto'

type Props = {
  modelValue: string | Date | null
  label: string
  disabled?: boolean
  min?: string | null
  max?: string | null
  error?: boolean
  errorMessages?: string[] | string
  firstDayOfWeek?: number
  placeholder?: string
  variant?: 'outlined' | 'filled' | 'plain' | 'underlined' | 'solo' | 'solo-inverted' | 'solo-filled'
  density?: 'default' | 'comfortable' | 'compact'
  view?: 'date' | 'month' | 'year'
  hideHeader?: boolean
  hideDetails?: HideDetails
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  min: null,
  max: null,
  error: false,
  errorMessages: () => [],
  firstDayOfWeek: 1,
  placeholder: 'DD.MM.YYYY',
  variant: 'outlined',
  density: 'comfortable',
  view: 'date',
  hideHeader: false,
  // keep default identical to Vuetify: do not hide unless asked
  hideDetails: 'auto' as HideDetails,
})

const emit = defineEmits<{ (e: 'update:modelValue', v: string | null): void }>()
const menu = ref(false)

function isValid(d: Date) { return d instanceof Date && !isNaN(d.getTime()) }

function toDate(val: string | Date | null): Date | null {
  if (!val) return null
  if (val instanceof Date) return isValid(val) ? val : null
  const dot = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(val)
  if (dot) {
    const [, dd, mm, yyyy] = dot
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
    return isValid(d) ? d : null
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val)
  if (iso) {
    const [, yyyy, mm, dd] = iso
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
    return isValid(d) ? d : null
  }
  return null
}

function formatDDMMYYYY(date: Date) {
  const d = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const y = date.getFullYear()
  return `${d}.${m}.${y}`
}

function formatYYYYMMDD(date: Date) {
  const d = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const y = date.getFullYear()
  return `${y}-${m}-${d}`
}

const display = computed(() => {
  const d = toDate(props.modelValue)
  return d ? formatDDMMYYYY(d) : ''
})

const pickerValue = computed<string | null>({
  get: () => {
    const d = toDate(props.modelValue)
    return d ? formatYYYYMMDD(d) : null
  },
  set: (v) => {
    const d = toDate(v as any)
    emit('update:modelValue', d ? formatDDMMYYYY(d) : null)
  },
})

const maxDate = computed(() => props.max ?? undefined)
const minDate = computed(() => props.min ?? undefined)

function closeOnPick() { menu.value = false }
</script>

<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
  >
    <template #activator="{ props: activatorProps }">
      <v-text-field
        v-bind="activatorProps"
        :model-value="display"
        :label="label"
        :placeholder="placeholder"
        :variant="variant"
        :density="density"
        readonly
        :error="error"
        :error-messages="errorMessages"
        :hide-details="hideDetails"
      />
    </template>

    <v-locale-provider locale="is">
      <v-date-picker
        v-model="pickerValue"
        :min="minDate"
        :max="maxDate"
        :title="''"
        :header="''"
        :first-day-of-week="firstDayOfWeek"
        :view="view"
        :hide-header="hideHeader"
        :disabled="props.disabled"
        @update:model-value="closeOnPick"
      />
    </v-locale-provider>
  </v-menu>
</template>

<style>
.v-picker-title, .v-picker__header { display: none; }
</style>
